"use client"

import { useCallback, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { api } from '@/core/api/queries';
import { useReportPage } from '@/shared/hooks';
import { EditionSelector } from '@/shared/components/tables/EditionSelector';
import RefreshButton from '@/shared/components/tables/RefreshButton';
import { Button } from '@/components/ui/button';
import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import type { ChartConfig } from '@/components/ui/chart';

// ==========================================
// CHART GEOMETRY
// ==========================================
// Valorile vin din directia A a planului de polish.
// → docs/design-system/REPORT_POLISH_PLAN.md, „Specificatia directiei A"

/** Inaltimea unui rand de date. Bara ocupa BAR_SIZE, restul e aer. */
const ROW_BAND = 48;
/** Grosimea barei. Peste ~24px graficul devine un bloc continuu. */
const BAR_SIZE = 22;
/** Spatiu pentru axa de sus + marginile verticale. */
const AXIS_GUTTER = 44;
/** Latimea coloanei de nume (rang + denumire), in afara zonei barei. */
const NAME_COL = 336;
/** Latimea rezervata etichetei de valoare, la dreapta barei. */
const VALUE_COL = 110;
/** Numarul de intervale de pe axa: 4 diviziuni etichetate (0 + 3). */
const AXIS_DIVISIONS = 3;

// Geometria interioara a etichetei de rand. Toate raman numerice: intra in
// atribute SVG (<g transform>, <text x>, <foreignObject width>), unde CSS-ul
// nu are ce cauta.
/** Marginea din dreapta a coloanei de rang — rangul e aliniat la dreapta pe ea. */
const RANK_COL_EDGE = 22;
/** De unde incepe denumirea, masurat de la marginea stanga a coloanei de nume. */
const NAME_INSET = 32;
/** Aer intre denumire si inceputul scalei, ca textul sa nu atinga prima bara. */
const NAME_TRAIL = 16;
/** Aer intre valoare si marginea din dreapta a coloanei de valori. */
const VALUE_TRAIL = 8;
/** Cate randuri de sus primesc accentul de brand in loc de gri. */
const TOP_RANK_HIGHLIGHT = 3;
/** Raza capatului de bara, in px. Oglindeste --radius-sm (4px) din main.css. */
const BAR_RADIUS = 4;

const formatRo = (value: number) => value.toLocaleString('ro-RO');

/**
 * Eticheta unui rand: rangul si denumirea scolii, in afara barei.
 *
 * Recharts randeaza tick-urile ca SVG, unde `text-overflow: ellipsis` nu exista.
 * De aceea numele merge intr-un <foreignObject>, ca sa se trunchieze corect si
 * sa primeasca un `title` cu denumirea completa la hover.
 */
function SchoolTick({ x, y, payload, index }: any) {
    return (
        <g transform={`translate(${x - NAME_COL},${y - ROW_BAND / 2})`}>
            <text
                x={RANK_COL_EDGE}
                y={ROW_BAND / 2}
                dy="0.32em"
                textAnchor="end"
                className="ecotic-chart-rank"
                style={{
                    // `fill` ramane inline, singurul stil de aici care nu poate urca
                    // in CSS: ChartContainer are
                    // [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground
                    // (components/ui/chart.tsx), un selector descendent care bate
                    // orice utility `fill-*` de o clasa. Valoarea e tot un token.
                    fill: index < TOP_RANK_HIGHLIGHT
                        ? 'var(--color-magenta)'
                        : 'var(--color-gray-400)',
                }}
            >
                {index + 1}
            </text>
            <foreignObject
                x={NAME_INSET}
                y={0}
                width={NAME_COL - NAME_INSET - NAME_TRAIL}
                height={ROW_BAND}
            >
                <div
                    title={payload.value}
                    className="ecotic-chart-name text-dark w-full truncate"
                    // Ramane inline: e inaltimea benzii exprimata ca lungime, adica
                    // geometrie derivata din ROW_BAND, exact ca y={ROW_BAND / 2} de
                    // mai sus. Urcata in CSS ar duplica constanta in doua fisiere.
                    style={{ lineHeight: `${ROW_BAND}px` }}
                >
                    {payload.value}
                </div>
            </foreignObject>
        </g>
    );
}

/**
 * Valoarea randului, intr-o coloana proprie la dreapta scalei.
 *
 * E o axa, nu un <LabelList position="right">: asa valorile stau aliniate pe o
 * margine comuna, in loc sa pluteasca fiecare la capatul barei ei.
 */
function ValueTick({ x, y, index, values }: any) {
    return (
        <text
            x={x + VALUE_COL - VALUE_TRAIL}
            y={y}
            dy="0.32em"
            textAnchor="end"
            className="ecotic-chart-value"
            // vezi nota despre `fill` din SchoolTick
            style={{ fill: 'var(--color-dark)' }}
        >
            {formatRo(values[index] ?? 0)}
        </text>
    );
}

/**
 * O valoare din banda de statistici a editiei.
 *
 * Separatorul e o bordura la stanga; containerul compenseaza cu -ml-5, ca prima
 * valoare sa nu aiba linie in fata.
 */
function Stat({ label, value, hero = false }: { label: string; value: string; hero?: boolean }) {
    return (
        <div className="flex-1 min-w-38 border-l border-gray-200 px-5">
            <div className="ecotic-field-label mb-0">{label}</div>
            <div
                className={cn(
                    'mt-1',
                    hero
                        ? 'ecotic-stat-value-hero text-magenta'
                        : 'ecotic-stat-value text-dark'
                )}
            >
                {value}
            </div>
        </div>
    );
}

/**
 * Schools Graphic Activities Report Page
 *
 * Displays a horizontal Bar Chart showing activity metrics for top-ranked schools.
 *
 * Features:
 * - Edition selector with label
 * - Metric type selector with label (Totals/Counters)
 * - Sort by field selector with label (dynamic options based on metric type)
 * - Top N schools selector with label (10/20/50)
 * - Horizontal bar chart with custom labels (green bars from design system)
 * - Summary statistics (Total, Average, Max, Min)
 * - Role-based access (ADMIN + DBEST)
 *
 * Updated: 2025-11-09
 * - Changed from stacked monotone areas to single step chart
 * - Changed from step area to horizontal bar chart with custom labels
 * - Added filter labels for better UX
 * - Changed color to brand green (--color-magenta)
 */
export function SchoolsGraphicReportPage() {
    // ==========================================
    // 1. STATE
    // ==========================================

    const [topN, setTopN] = useState('10');
    const [metricType, setMetricType] = useState<'totals' | 'counters'>('totals');
    const [sortField, setSortField] = useState('pointsGrandTotal');

    // ==========================================
    // 2. DATA FETCHING (reusing useReportPage pattern)
    // ==========================================

    const { report, handleExport, editions, currentEdition, changeEdition } = useReportPage({
        queryHook: api.schools.statusReport,
        initialSort: [], // No server-side sort - fetch all schools
        initialPageSize: 1000, // Fetch all schools (max 510 in edition)
        exportEndpoint: '/api/schools/status-report',
        exportFilename: 'grafic_activitati_scoli',
        withEditions: true
    });

    // ==========================================
    // 2.5 SORT & FILTER HANDLERS (Client-Side)
    // ==========================================

    /**
     * Handle metric type change - updates metric type and sets smart sort field defaults
     */
    const handleMetricTypeChange = useCallback((value: 'totals' | 'counters') => {
        setMetricType(value);
        // Set smart defaults based on metric type
        setSortField(
            value === 'totals'
                ? 'pointsGrandTotal'  // Default to Total General for totals
                : 'countPracticalActivity'  // Default to Activități Aprobate for counters
        );
    }, []);

    /**
     * Handle sort field change - updates UI state only (sorting happens in chartData)
     */
    const handleSortChange = useCallback((newSortField: string) => {
        setSortField(newSortField);
    }, []);

    /**
     * Handle top N change - updates UI state only (limiting happens in chartData)
     */
    const handleTopNChange = useCallback((newTopN: string) => {
        setTopN(newTopN);
    }, []);

    // ==========================================
    // 3. CHART CONFIGURATION
    // ==========================================

    const chartConfig = {
        value: {
            label: metricType === 'totals' ? 'Punctaj Total' : 'Număr Aprobări',
            color: "var(--color-magenta)",
        },
        label: {
            color: "var(--background)",
        },
    } satisfies ChartConfig;

    // ==========================================
    // 4. DATA TRANSFORMATION
    // ==========================================

    const chartData = useMemo(() => {
        if (!report.data || report.data.length === 0) return [];

        // 1. CLIENT-SIDE SORT: Sort by selected field descending
        const sorted = [...report.data].sort((a, b) =>
            Number(b[sortField as keyof typeof a] || 0) - Number(a[sortField as keyof typeof a] || 0)
        );

        // 2. CLIENT-SIDE LIMIT: Take top N schools
        const topSchools = sorted.slice(0, parseInt(topN));

        // 4. Transform to chart format - use backend-calculated values directly
        return topSchools.map(school => ({
            school: school.numeInstitutieInvatamant || 'N/A',
            value: Number(school[sortField as keyof typeof school] || 0),
        }));
    }, [report.data, sortField, topN]);

    // ==========================================
    // 4.5 CHART GEOMETRY
    // ==========================================

    /**
     * Rounds a step up to the nearest 1 / 2 / 2.5 / 5 × 10^n.
     * Folosit ca sa obtinem diviziuni citibile (50.000), nu valori brute (45.493).
     */
    const niceStep = (raw: number) => {
        if (raw <= 0) return 1;
        const magnitude = 10 ** Math.floor(Math.log10(raw));
        const normalized = raw / magnitude;
        const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
        return step * magnitude;
    };

    /**
     * Domeniul axei X, rotunjit in sus.
     *
     * ⚠️ NU folosi domain={[0, 'dataMax']}: bara maxima ajunge exact pe marginea
     * zonei de plot, iar eticheta ei de valoare se deseneaza dincolo de clip si
     * apare taiata. → docs/design-system/REPORT_POLISH_PLAN.md, constatarea #1
     */
    const { axisMax, axisTicks, toateValorileSuntZero } = useMemo(() => {
        const dataMax = chartData.length ? Math.max(...chartData.map(d => d.value)) : 0;
        const step = niceStep(Math.max(dataMax, 1) / AXIS_DIVISIONS);
        const max = step * AXIS_DIVISIONS;
        return {
            axisMax: max,
            axisTicks: Array.from({ length: AXIS_DIVISIONS + 1 }, (_, i) => i * step),
            // `dataMax === 0` (nu `axisMax === 0`): axisMax e mereu > 0, prin
            // `Math.max(dataMax, 1)` de mai sus, tocmai ca axa sa ramana citibila
            // si cu toate valorile la zero.
            toateValorileSuntZero: dataMax === 0,
        };
    }, [chartData]);

    /** Inaltimea graficului creste cu numarul de randuri; banda ramane fixa. */
    const chartHeight = chartData.length * ROW_BAND + AXIS_GUTTER;

    // ==========================================
    // 5. SUMMARY STATISTICS (EDITION-WIDE)
    // ==========================================

    /**
     * Edition-wide statistics - calculates from ALL schools in the edition
     * (not just the displayed top N schools in the chart)
     */
    const editionStats = useMemo(() => {
        if (!report.data || report.data.length === 0) return null;

        // Extract values for the currently selected sortField from ALL schools
        const values = report.data.map(school =>
            Number(school[sortField as keyof typeof school] || 0)
        );

        const total = values.reduce((sum, val) => sum + val, 0);
        const average = total / values.length;
        const max = Math.max(...values);
        const min = Math.min(...values);

        return {
            total,
            average,
            max,
            min,
            count: values.length, // Total schools in edition
            label: metricType === 'totals' ? 'Puncte' : 'Aprobări',
        };
    }, [report.data, sortField, metricType]);

    // ==========================================
    // 6. RENDER
    // ==========================================

    // Suprafata e un <div>, nu un <Card>: shadcn pune pe Card `bg-card` (verde
    // deschis) si `rounded-xl`, iar ambele se emit DUPA `ecotic-report-surface` in
    // CSS-ul construit, deci ar castiga cascada si ar readuce exact fundalul pe
    // care il scoate acest PR. Clasele de layout de mai jos replica restul bazei
    // de Card (flex-col, gap-6, py-6, text-card-foreground = text-dark).
    // CardHeader/CardContent nu au nevoie de un stramos Card.
    // → docs/design-system/REPORT_POLISH_PLAN.md, faza 3
    return (
        <div className="ecotic-report-surface text-dark flex flex-1 min-h-0 flex-col gap-6 py-6 m-4 pb-0 overflow-hidden">
                    {/* ===== HEADER WITH CONTROLS ===== */}
                    <CardHeader>
                        <div className="flex items-start justify-between">
                            <div>
                                <CardTitle>Grafic Activități Școli</CardTitle>
                                <CardDescription>
                                    Top {topN} școli sortate după {
                                        sortField === 'pointsGrandTotal' ? 'punctaj total general' :
                                        metricType === 'totals' ? 'punctaje' : 'număr aprobări'
                                    }
                                </CardDescription>
                            </div>

                            {/* Aceeasi bara de actiuni ca pe rapoartele tabelare.
                                Pagina de grafic declara exportEndpoint de la inceput,
                                dar nu avea cu ce sa-l porneasca.
                                → docs/design-system/REPORT_POLISH_PLAN.md, constatarea #11 */}
                            <div className="flex flex-none items-center gap-2">
                                <RefreshButton
                                    handleRefresh={report.refetch}
                                    disabled={report.loading}
                                />
                                <Button onClick={handleExport} disabled={report.loading}>
                                    Export
                                </Button>
                            </div>
                        </div>

                        <div className="graphic-report-controls">
                            {/* Edition Filter with Label */}
                            <div className="flex flex-col">
                                <EditionSelector
                                    editions={editions || []}
                                    currentEditionId={currentEdition?.id}
                                    onChange={changeEdition || (() => {})}
                                    disabled={report.loading}
                                />
                            </div>

                            {/* Metric Type Filter with Label */}
                            <div className="flex flex-col">
                                <label className="ecotic-field-label">
                                    Tip Metrică
                                </label>
                                <Select
                                    value={metricType}
                                    onValueChange={handleMetricTypeChange}
                                >
                                    <SelectTrigger className="ecotic-field-control" aria-label="Tip Metrică">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="totals">Punctaje Totale</SelectItem>
                                        <SelectItem value="counters">Număr Aprobări</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Sort By Filter with Label */}
                            <div className="flex flex-col">
                                <label className="ecotic-field-label">
                                    Sortare După
                                </label>
                                <Select value={sortField} onValueChange={handleSortChange}>
                                    <SelectTrigger className="ecotic-field-control" aria-label="Sortare După">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {/* Total General only available for Punctaje Totale (not for Număr Aprobări) */}
                                        {metricType === 'totals' && (
                                            <SelectItem value="pointsGrandTotal">Total General</SelectItem>
                                        )}
                                        {metricType === 'totals' ? (
                                            <>
                                                <SelectItem value="pointsPracticalActivity">Activități Practice</SelectItem>
                                                <SelectItem value="pointsHomework">Teme</SelectItem>
                                                <SelectItem value="pointsMonthlyChallenge">Provocări</SelectItem>
                                                <SelectItem value="pointsWasteCollectionTotal">Colectare Deșeuri</SelectItem>
                                            </>
                                        ) : (
                                            <>
                                                <SelectItem value="countPracticalActivity">Activități Aprobate</SelectItem>
                                                <SelectItem value="countHomework">Teme Aprobate</SelectItem>
                                                <SelectItem value="countMonthlyChallenge">Provocări Aprobate</SelectItem>
                                            </>
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Top N Filter with Label */}
                            <div className="flex flex-col">
                                <label className="ecotic-field-label">
                                    Număr Școli
                                </label>
                                <Select value={topN} onValueChange={handleTopNChange}>
                                    <SelectTrigger className="ecotic-field-control" aria-label="Număr Școli">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="10">Top 10 Școli</SelectItem>
                                        <SelectItem value="20">Top 20 Școli</SelectItem>
                                        <SelectItem value="50">Top 50 Școli</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardHeader>

                    {/* ===== CHART CONTENT ===== */}
                    {/* Sub `md`, `flex-1 min-h-0` intr-o coloana de inaltime fixa lasa
                        graficului ce ramane dupa antet si banda de statistici: masurat la
                        360x800, **78px vizibili pentru 524px de continut** — zece bare
                        intr-o fanta. `min-h-[60vh]` sub `md` ii da 480px, adica un grafic
                        pe care il poti citi; derularea proprie a graficului ramane, si e
                        singura de pe ecran. De la `md` in sus, nimic nu se schimba. */}
                    <CardContent className="min-h-[60vh] flex-1 overflow-auto md:min-h-0">
                        {report.loading ? (
                            <div className="graphic-report-loading">
                                Se încarcă datele...
                            </div>
                        ) : chartData.length === 0 ? (
                            <div className="graphic-report-loading">
                                Nu există date disponibile pentru ediția selectată
                            </div>
                        ) : (
                            <ChartContainer
                                config={chartConfig}
                                className="graphic-report-card"
                                style={{ height: chartHeight }}
                            >
                                <BarChart
                                    accessibilityLayer
                                    data={chartData}
                                    layout="vertical"
                                    barCategoryGap={ROW_BAND - BAR_SIZE}
                                    margin={{ left: 0, right: 0, top: 8, bottom: 8 }}
                                >
                                    <CartesianGrid horizontal={false} />

                                    {/* Denumirea scolii, in coloana proprie la stanga scalei */}
                                    <YAxis
                                        dataKey="school"
                                        type="category"
                                        tickLine={false}
                                        axisLine={false}
                                        width={NAME_COL}
                                        interval={0}
                                        tick={<SchoolTick />}
                                    />

                                    {/* Coloana de valori, aliniata la dreapta */}
                                    <YAxis
                                        yAxisId="value"
                                        dataKey="school"
                                        type="category"
                                        orientation="right"
                                        tickLine={false}
                                        axisLine={false}
                                        width={VALUE_COL}
                                        interval={0}
                                        tick={<ValueTick values={chartData.map(d => d.value)} />}
                                    />

                                    {/* Axa cu diviziuni etichetate; fara ea grila nu spune nimic */}
                                    <XAxis
                                        dataKey="value"
                                        type="number"
                                        domain={[0, axisMax]}
                                        ticks={axisTicks}
                                        orientation="top"
                                        tickLine={false}
                                        axisLine={false}
                                        tickMargin={6}
                                        className="ecotic-chart-axis"
                                        tickFormatter={formatRo}
                                    />

                                    <ChartTooltip
                                        cursor={false}
                                        content={<ChartTooltipContent indicator="line" />}
                                    />

                                    <Bar
                                        dataKey="value"
                                        layout="vertical"
                                        fill="var(--color-value)"
                                        barSize={BAR_SIZE}
                                        radius={[0, BAR_RADIUS, BAR_RADIUS, 0]}
                                        // Track-ul de fundal e desenat mereu la latimea INTREGII axe,
                                        // indiferent de valoarea randului — util cand exista o bara
                                        // colorata deasupra, care arata cat s-a "umplut" din el. Cu
                                        // toate valorile la zero, nicio bara colorata nu se mai
                                        // deseneaza (Recharts omite un path de latime 0), si raman
                                        // doar cele N track-uri pline, identice — se citesc ca "toata
                                        // lumea a atins maximul", exact opusul realitatii. Fara
                                        // track cand nu exista nimic de comparat.
                                        background={toateValorileSuntZero ? false : { fill: 'var(--color-light)', radius: [0, BAR_RADIUS, BAR_RADIUS, 0] } as never}
                                    />
                                </BarChart>
                            </ChartContainer>
                        )}
                    </CardContent>

                    {/* ===== FOOTER WITH SUMMARY STATS (EDITION-WIDE) ===== */}
                    {editionStats && (
                        <div className="ecotic-report-rail mt-auto bg-gray-50 px-6 py-4">
                            <div className="flex flex-wrap items-stretch -ml-5">
                                <Stat
                                    label={`Total ${editionStats.label.toLowerCase()} (ediție)`}
                                    value={formatRo(editionStats.total)}
                                    hero
                                />
                                <Stat
                                    label={`Medie / școală (${editionStats.count})`}
                                    value={formatRo(Math.round(editionStats.average))}
                                />
                                <Stat label="Maxim" value={formatRo(editionStats.max)} />
                                <Stat label="Minim" value={formatRo(editionStats.min)} />
                            </div>
                        </div>
                    )}
        </div>
    );
}
